import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { formatDate } from '../../data/mockData';
import { Eye, FileText, AlertCircle } from 'lucide-react';

const TemplateViewerDrawer = ({ open, onOpenChange, template }) => {
  if (!template) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Effective': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Draft': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Retired': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl bg-white border-slate-200">
        <SheetHeader>
          <SheetTitle className="text-slate-900 flex items-center gap-2">
            <Eye className="h-5 w-5" />
            {template.name}
          </SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-80px)] mt-4">
          <div className="space-y-6 pr-4">
            {/* Metadata */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">ID</p>
                  <p className="text-sm font-mono text-[#fd4816]">{template.id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Version</p>
                  <p className="text-sm text-slate-800">v{template.version}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Status</p>
                  <Badge className={`${getStatusColor(template.status)} text-xs`}>
                    {template.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Category</p>
                  <p className="text-sm text-slate-800">{template.category}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Frequency</p>
                  <p className="text-sm text-slate-800">{template.frequency}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Criticality</p>
                  <p className="text-sm text-slate-800">{template.criticality}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Effective Date</p>
                  <p className="text-sm text-slate-800">
                    {template.effectiveDate ? formatDate(template.effectiveDate) : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Questions</p>
                  <p className="text-sm text-slate-800">{template.questions || template.questionsList?.length || 0}</p>
                </div>
              </div>
            </div>

            {/* Questions List */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-900">Questions</h3>
              </div>
              {template.questionsList && template.questionsList.length > 0 ? (
                <div className="space-y-2">
                  {template.questionsList.map((q, idx) => (
                    <div key={q.id} className="p-3 bg-slate-50 border border-slate-200 rounded-md">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs font-mono text-slate-500">Q{idx + 1}</span>
                        <div className="flex gap-1">
                          <Badge variant="outline" className="text-xs border-slate-200">
                            {q.responseType}
                          </Badge>
                          {q.failureCreatesDeviation && (
                            <Badge variant="outline" className="text-xs border-red-200 text-red-700">
                              Creates Deviation
                            </Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-slate-800 mb-2">{q.text}</p>
                      {q.evidenceRequired && q.evidenceRequired !== 'None' && (
                        <div className="flex items-center gap-2 mt-2">
                          <AlertCircle className="h-3 w-3 text-slate-500" />
                          <span className="text-xs text-slate-600">Evidence: {q.evidenceRequired}</span>
                        </div>
                      )}
                      {q.clauseReference && (
                        <div className="mt-2 pt-2 border-t border-slate-200">
                          <p className="text-xs text-slate-500">Clause Reference</p>
                          <p className="text-xs text-slate-700">{q.clauseReference}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 italic">No questions defined</p>
              )}
            </div>

            {/* Clause Mappings */}
            {template.linkedReqs && template.linkedReqs.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-slate-900">Linked Requirements</h3>
                <div className="flex flex-wrap gap-2">
                  {template.linkedReqs.map(req => (
                    <Badge key={req} variant="outline" className="text-xs border-slate-200">
                      {req}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default TemplateViewerDrawer;

